<template>
  <teleport to="body">
    <div
      class="fixed inset-0 z-[99999] flex items-end justify-center bg-[#07153473] px-5"
      style="backdrop-filter: blur(5px)"
      @click.self="$emit('close')"
    >
      <div class="drawer-shell relative w-full">
        <button
          type="button"
          class="drawer-close"
          :aria-label="$t('ova-restaurant.orderEditDrawer.close')"
          @click="$emit('close')"
        >
          <span>&times;</span>
        </button>

        <div class="drawer-panel">
          <div class="drawer-handle"></div>

          <div class="drawer-header">
            <h2>{{ $t('ova-restaurant.orderEditDrawer.title') }}</h2>
            <p>{{ orderLabel }}</p>
            <p class="drawer-hint">{{ $t('ova-restaurant.orderEditDrawer.finalHint') }}</p>
          </div>

          <div class="changes-list">
            <div v-if="!hasChanges" class="change-block">
              {{ $t('ova-restaurant.orderEditDrawer.fallback') }}
            </div>

            <div v-if="infoChanges.length" class="change-block">
              <p class="change-title">{{ $t('ova-restaurant.orderEditDrawer.orderInfo') }}</p>
              <div v-for="change in infoChanges" :key="`info-${change.key}`" class="change-detail-row">
                <span>{{ change.label }}</span>
                <span class="change-value">
                  <span>{{ change.oldValue }}</span>
                  <span class="change-arrow" aria-hidden="true">➜</span>
                  <span>{{ change.newValue }}</span>
                </span>
              </div>
            </div>

            <div v-if="added.length" class="change-block">
              <p class="change-title">{{ $t('ova-restaurant.orderEditDrawer.added') }}</p>
              <div v-for="dish in added" :key="dish.key" class="dish-change">
                <div class="change-row">
                  <span>{{ dish.name }}</span>
                  <span v-if="dish.quantity">+{{ dish.quantity }}</span>
                </div>
                <p v-for="detail in dish.details" :key="detail.key" class="change-detail">
                  <span>{{ detail.label }}: </span>
                  <span v-if="detail.singleValue">{{ detail.singleValue }}</span>
                  <span v-else class="change-value">
                    <span>{{ detail.oldValue }}</span>
                    <span class="change-arrow" aria-hidden="true">➜</span>
                    <span>{{ detail.newValue }}</span>
                  </span>
                </p>
              </div>
            </div>

            <div v-if="removed.length" class="change-block">
              <p class="change-title">{{ $t('ova-restaurant.orderEditDrawer.removed') }}</p>
              <div v-for="dish in removed" :key="dish.key" class="dish-change">
                <div class="change-row">
                  <span>{{ dish.name }}</span>
                  <span v-if="dish.quantity">-{{ dish.quantity }}</span>
                </div>
                <p v-for="detail in dish.details" :key="detail.key" class="change-detail">
                  <span>{{ detail.label }}: </span>
                  <span v-if="detail.singleValue">{{ detail.singleValue }}</span>
                  <span v-else class="change-value">
                    <span>{{ detail.oldValue }}</span>
                    <span class="change-arrow" aria-hidden="true">➜</span>
                    <span>{{ detail.newValue }}</span>
                  </span>
                </p>
              </div>
            </div>

            <div v-if="changed.length" class="change-block">
              <p class="change-title">{{ $t('ova-restaurant.orderEditDrawer.changed') }}</p>
              <div v-for="dish in changed" :key="dish.key" class="dish-change">
                <div class="change-row">
                  <span>{{ dish.name }}</span>
                </div>
                <p v-for="detail in dish.details" :key="detail.key" class="change-detail">
                  <span>{{ detail.label }}: </span>
                  <span v-if="detail.singleValue">{{ detail.singleValue }}</span>
                  <span v-else class="change-value">
                    <span>{{ detail.oldValue }}</span>
                    <span class="change-arrow" aria-hidden="true">➜</span>
                    <span>{{ detail.newValue }}</span>
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div class="drawer-actions">
            <button type="button" class="drawer-secondary" @click="$emit('close')">
              {{ $t('ova-restaurant.orderEditDrawer.close') }}
            </button>
            <button type="button" class="drawer-primary" @click="viewFinalOrder">
              {{ $t('ova-restaurant.orderEditDrawer.viewFinal') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
const props = defineProps<{
  orderEditLog: any
}>()

const emit = defineEmits<{
  (event: 'close'): void
}>()

const dishesStore = useDishesStore()
const { symbol } = storeToRefs(dishesStore)
const { t: $t } = useI18n()
const router = useRouter()
const route = useRoute()
let previousBodyOverflow = ''
let previousBodyPosition = ''
let previousBodyTop = ''
let previousBodyWidth = ''
let lockedScrollY = 0

const order = computed(() => props.orderEditLog?.order)
const eventChanges = computed(() => props.orderEditLog?.changes ?? null)
const resourceChanges = computed(() => order.value?.dish_changes ?? {})

const fieldLabels: Record<string, string> = {
  quantity: 'quantity',
  price: 'price',
  total: 'total',
  comment: 'comment',
  our_comment: 'ourComment',
  add_ons: 'addOns',
  option_combain_id: 'option',
  type: 'type',
  table_id: 'table',
  delivery_full_name: 'deliveryFullName',
  delivery_email: 'deliveryEmail',
  delivery_phone: 'deliveryPhone',
  delivery_address: 'deliveryAddress',
}

const formatAmount = (value: number | string | null | undefined) => {
  const amount = Number(value || 0)
  return `${symbol.value}${amount.toFixed(2)}`
}

const formatValue = (key: string, value: unknown) => {
  if (value === null || value === undefined || value === '') return $t('ova-restaurant.orderEditDrawer.emptyValue')
  if (key === 'price' || key === 'total') return formatAmount(value as number | string)
  if (key === 'type') return String(value).replace(/_/g, ' ')
  if (Array.isArray(value)) return value.map((item) => formatValue(key, item)).join(', ')
  if (typeof value === 'object') return Object.values(value as Record<string, unknown>).map((item) => formatValue(key, item)).join(', ')
  return String(value)
}

const getFieldLabel = (key: string) => {
  const labelKey = fieldLabels[key] ?? key
  return $t(`ova-restaurant.orderEditDrawer.fields.${labelKey}`)
}

const makeDetail = (key: string, value: any) => ({
  key,
  label: getFieldLabel(key),
  oldValue: formatValue(key, value?.old),
  newValue: formatValue(key, value?.new),
  singleValue: null as string | null,
})

const makeAddedPriceDetail = (value: unknown) => ({
  key: 'price',
  label: getFieldLabel('price'),
  oldValue: '',
  newValue: '',
  singleValue: `+ ${formatAmount(value as number | string)}`,
})

const makeRemovedPriceDetail = (value: unknown) => ({
  key: 'price',
  label: getFieldLabel('price'),
  oldValue: '',
  newValue: '',
  singleValue: `- ${formatAmount(value as number | string)}`,
})

const getRemovedDishPrice = (dish: any) => {
  const removedDishes = resourceChanges.value?.removed
  if (!Array.isArray(removedDishes)) return null

  const removedDish = removedDishes.find((item: any) => {
    return (
      (dish.item_id && item.dish_id && dish.item_id == item.dish_id) ||
      (dish.name && item.name && dish.name === item.name)
    )
  })

  return removedDish?.price ?? null
}

const eventDishChanges = computed(() => {
  const dishes = eventChanges.value?.dishes
  if (!Array.isArray(dishes)) return null

  return dishes.map((dish: any, index: number) => {
    const quantityChange = dish.changes?.quantity
    const oldQuantity = Number(quantityChange?.old ?? Number.NaN)
    const newQuantity = Number(quantityChange?.new ?? Number.NaN)
    const type = oldQuantity === 0 ? 'added' : newQuantity === 0 ? 'removed' : 'changed'
    const details = Object.entries(dish.changes ?? {})
      .filter(([key]) => !(key === 'quantity' && (type === 'added' || type === 'removed')))
      .map(([key, value]: [string, any]) => {
        if (type === 'added' && key === 'price') return makeAddedPriceDetail(value?.new)
        if (type === 'removed' && key === 'price') return makeRemovedPriceDetail(value?.old)
        return makeDetail(key, value)
      })

    if (type === 'removed' && !details.some((detail) => detail.key === 'price')) {
      const removedPrice = getRemovedDishPrice(dish)
      if (removedPrice) {
        details.push(makeRemovedPriceDetail(removedPrice))
      }
    }

    return {
      key: `${dish.item_id ?? index}-${dish.name}-${type}`,
      name: dish.name,
      quantity: Number.isFinite(newQuantity - oldQuantity) ? Math.abs(newQuantity - oldQuantity) : null,
      details,
      type,
    }
  })
})

const oldResourceDishChanges = computed(() => ({
  added: (resourceChanges.value?.added ?? []).map((dish: any, index: number) => ({
    key: `added-${dish.id ?? index}-${dish.name}`,
    name: dish.name,
    quantity: dish.quantity,
    details: dish.price ? [makeAddedPriceDetail(dish.price)] : [],
    type: 'added',
  })),
  removed: (resourceChanges.value?.removed ?? []).map((dish: any, index: number) => ({
    key: `removed-${dish.id ?? index}-${dish.name}`,
    name: dish.name,
    quantity: dish.quantity,
    details: dish.price ? [makeRemovedPriceDetail(dish.price)] : [],
    type: 'removed',
  })),
  changed: (resourceChanges.value?.changed ?? []).map((dish: any, index: number) => {
    const details = [
      dish.quantity_before !== dish.quantity_after ? makeDetail('quantity', { old: dish.quantity_before, new: dish.quantity_after }) : null,
      dish.price_before !== dish.price_after ? makeDetail('price', { old: dish.price_before, new: dish.price_after }) : null,
      dish.note_before !== dish.note_after ? makeDetail('comment', { old: dish.note_before, new: dish.note_after }) : null,
      dish.our_note_before !== dish.our_note_after ? makeDetail('our_comment', { old: dish.our_note_before, new: dish.our_note_after }) : null,
    ].filter((detail): detail is ReturnType<typeof makeDetail> => Boolean(detail))

    return {
      key: `changed-${dish.id ?? index}-${dish.name}`,
      name: dish.name,
      quantity: null,
      details,
      type: 'changed',
    }
  }),
}))

const added = computed(() => eventDishChanges.value?.filter((dish) => dish.type === 'added') ?? oldResourceDishChanges.value.added)
const removed = computed(() => eventDishChanges.value?.filter((dish) => dish.type === 'removed') ?? oldResourceDishChanges.value.removed)
const changed = computed(() => eventDishChanges.value?.filter((dish) => dish.type === 'changed') ?? oldResourceDishChanges.value.changed)

const infoChanges = computed(() => {
  const information = eventChanges.value?.information
  if (!information || typeof information !== 'object') return []
  return Object.entries(information).map(([key, value]) => makeDetail(key, value))
})

const hasChanges = computed(() => {
  return Boolean(
    eventDishChanges.value?.length ||
    infoChanges.value.length ||
    resourceChanges.value?.has_changes ||
    added.value.length ||
    removed.value.length ||
    changed.value.length
  )
})

const orderLabel = computed(() => {
  const code = order.value?.short_code || props.orderEditLog?.order_id
  return code
    ? $t('ova-restaurant.orderEditDrawer.orderChangedWithCode', { code })
    : $t('ova-restaurant.orderEditDrawer.orderChangedGeneric')
})

const viewFinalOrder = async () => {
  const orderId = order.value?.id || props.orderEditLog?.order_id

  if (order.value) {
    dishesStore.showingSingleOrder = order.value
  }

  emit('close')

  if (orderId) {
    await router.push({
      path: `/orders/${orderId}`,
      query: route.query,
    })
  }
}

const lockPageScroll = () => {
  lockedScrollY = window.scrollY
  previousBodyOverflow = document.body.style.overflow
  previousBodyPosition = document.body.style.position
  previousBodyTop = document.body.style.top
  previousBodyWidth = document.body.style.width

  document.body.style.overflow = 'hidden'
  document.body.style.position = 'fixed'
  document.body.style.top = `-${lockedScrollY}px`
  document.body.style.width = '100%'
}

const unlockPageScroll = () => {
  document.body.style.overflow = previousBodyOverflow
  document.body.style.position = previousBodyPosition
  document.body.style.top = previousBodyTop
  document.body.style.width = previousBodyWidth
  window.scrollTo(0, lockedScrollY)
}

onMounted(() => {
  lockPageScroll()
})

onBeforeUnmount(() => {
  unlockPageScroll()
})
</script>

<style lang="scss" scoped>
.drawer-shell {
  padding-top: 56px;
}

.drawer-close {
  position: absolute;
  top: 0;
  right: 12px;
  z-index: 2;
  display: flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #ffffff;
  color: #505c78;
  box-shadow: 0 10px 28px rgba(7, 21, 52, 0.12);
}

.drawer-close span {
  font-size: 36px;
  font-weight: 300;
  line-height: 1;
  transform: translateY(-2px);
}

.drawer-panel {
  display: flex;
  max-height: min(78dvh, 680px);
  flex-direction: column;
  gap: 22px;
  border-radius: 28px 28px 0 0;
  background: #ffffff;
  padding: 26px 30px 24px;
  box-shadow: 0 -18px 48px rgba(7, 21, 52, 0.16);
}

.drawer-handle {
  align-self: center;
  width: 136px;
  height: 5px;
  border-radius: 999px;
  background: #ccd4e5;
}

.drawer-header {
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
}

.drawer-header h2 {
  color: #071534;
  font-size: 26px;
  font-weight: 800;
  line-height: 32px;
}

.drawer-header p {
  color: #505c78;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}

.drawer-header .drawer-hint {
  color: #738099;
  font-size: 14px;
  line-height: 20px;
}

.changes-list {
  display: flex;
  min-height: 0;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding-right: 2px;
  scrollbar-width: none;
}

.changes-list::-webkit-scrollbar {
  display: none;
}

.change-block {
  border: 1px solid #e7ebf3;
  border-radius: 18px;
  background: #f8f9fc;
  padding: 14px;
  color: #071534;
}

.change-title {
  margin-bottom: 8px;
  color: #759331;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.dish-change {
  display: flex;
  flex-direction: column;
  gap: 5px;
  border-bottom: 1px solid #e7ebf3;
  padding: 10px 0;
}

.dish-change:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.change-row,
.change-detail-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.change-row {
  color: #071534;
  font-size: 15px;
  font-weight: 800;
}

.change-detail,
.change-detail-row {
  color: #505c78;
  font-size: 13px;
  font-weight: 600;
  line-height: 19px;
  word-break: break-word;
}

.change-value {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.change-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #759331;
  font-size: 16px;
  font-weight: 700;
  line-height: 16px;
  width: 16px;
  height: 16px;
  margin: 0 1px;
  vertical-align: middle;
  transform: translateY(-1px);
}

.change-detail-row {
  border-bottom: 1px solid #e7ebf3;
  padding: 8px 0;
}

.change-detail-row:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.drawer-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
}

.drawer-secondary,
.drawer-primary {
  min-height: 58px;
  border-radius: 999px;
  padding: 0 18px;
  font-size: 18px;
  font-weight: 800;
}

.drawer-secondary {
  border: 1.5px solid #071534;
  color: #071534;
}

.drawer-primary {
  background: #071011;
  color: #ffffff;
}

@media (max-width: 420px) {
  .drawer-panel {
    padding: 26px 24px 22px;
  }

  .drawer-header h2 {
    font-size: 24px;
    line-height: 30px;
  }

  .drawer-actions {
    gap: 10px;
  }

  .drawer-secondary,
  .drawer-primary {
    min-height: 54px;
    padding: 0 12px;
    font-size: 16px;
  }
}
</style>
